import type { PartnerResponse } from '@palaccount/contracts';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/**
 * NFR-14 — the five colour meanings, the same on every screen: grey = not
 * started or a draft, blue = active/in progress, amber = waiting, green =
 * finished/good/money in, red = a problem/late/cancelled. Every map in this
 * file is that scheme applied to one screen's lifecycle, with each colour
 * carrying its dark-theme pair.
 *
 * NFR-39 / GEN-61 / GEN-186 — "colour must never be the only way something
 * is said" — is why every chip renders `t(...)` TEXT inside the colour: the
 * status is always a word first, and the colour is the redundant channel.
 * GEN-62 and GEN-63 hold in every map below: no draft is ever green, no
 * cancelled state is ever plain grey, and red marks problems and money owed
 * — never routine outflow (GEN-64: the cash dashboard draws spending in
 * neutral grey, red only for a bad net). GEN-66: where a record carries
 * more than one status, the DOCUMENT status chip is the one these
 * components draw first.
 */
const CHIP =
  'inline-flex items-center whitespace-nowrap rounded-full border border-transparent px-2.5 py-0.5 text-xs font-semibold';

/**
 * `credited` (wave 4A) is the sales return's fourth state — its full credit
 * applied to invoices — and wears the "finished, money in" green the debit
 * note's own Credited chip wears (PURCHASE_RETURN_CLASSES below). Only the
 * credit note ever carries it; every other document stays on the three.
 */
export type DocStatus = 'draft' | 'posted' | 'credited' | 'cancelled';

/**
 * SAL-00 §7 — the shared document lifecycle: Draft grey, Posted blue,
 * Cancelled red and faded. FIN-03 §7 draws its Posted GREEN (money arrived),
 * so the voucher passes `postedTone="green"`.
 *
 * ── "FADED" IS A PROPERTY OF THE FILL, NOT OF THE CHIP ────────────────────
 *
 * `opacity-70` on the whole chip composited fill AND ink toward the surface
 * together, so the two closed on each other: the Cancelled chip measured
 * 3.69:1 against a bar of 4.5 (2.14:1 read in linear light), and the Closed
 * chip below was worse. `bg-red-100/70` paints the IDENTICAL fill pixel —
 * same colour, same 70% — and leaves the ink at full strength, which takes
 * the pair to 5.59:1 while looking the same amount of spent.
 *
 * No colour value moved to achieve that, and none may: NFR-39's problem here
 * was never which red, it was that a group opacity fades the thing you are
 * reading along with the thing you are reading it on. See
 * `src/test/faded-chip-contrast.test.ts`, which measures both.
 */
const DOC_CLASSES: Record<DocStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  posted: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  credited: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  cancelled: 'bg-red-100/70 text-red-700 dark:bg-red-950/70 dark:text-red-300',
};

const POSTED_GREEN = DOC_CLASSES.credited;

export function DocStatusChip({
  status,
  postedTone = 'blue',
  className,
}: {
  status: DocStatus;
  postedTone?: 'blue' | 'green';
  className?: string;
}) {
  const { t } = useTranslation();
  const tone = status === 'posted' && postedTone === 'green' ? POSTED_GREEN : DOC_CLASSES[status];
  return <span className={cn(CHIP, tone, className)}>{t(`common.status.${status}`)}</span>;
}

/**
 * PUR-04 §7 — the debit note's own lifecycle, which is NOT the shared three.
 * The screen's set is Draft grey, Approved blue and Credited green; approving
 * IS posting here (§6's green button reads "Post return"), so `posted` wears
 * §7's Approved colour. BR-PUR-04-01 and BR-PUR-04-02 add the two the
 * prototype lacked and gave reasons for both: Cancelled, so a note raised by
 * mistake can be reversed rather than left standing, and Refunded, "because
 * money coming back into the bank is not the same as owing the supplier less".
 */
export type PurchaseReturnChipStatus =
  | 'draft'
  | 'posted'
  | 'credited'
  | 'refunded'
  | 'cancelled';

const PURCHASE_RETURN_CLASSES: Record<PurchaseReturnChipStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  posted: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  credited: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  refunded: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  // Faded on the fill only — see the note on DOC_CLASSES above.
  cancelled: 'bg-red-100/70 text-red-700 dark:bg-red-950/70 dark:text-red-300',
};

export function PurchaseReturnStatusChip({
  status,
  className,
}: {
  status: PurchaseReturnChipStatus;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <span className={cn(CHIP, PURCHASE_RETURN_CLASSES[status], className)}>
      {t(`preturn.status.${status}`)}
    </span>
  );
}

/**
 * SAL-01 §7 — the SIX defined quotation chips (BR-SAL-01-01 removed the two
 * chipless ones): Draft grey, Sent blue, Accepted green, Rejected red,
 * Expired amber, Converted green.
 *
 * EXPIRED IS AMBER PRODUCT-WIDE, AND THAT IS NOW RULED RATHER THAN INHERITED.
 * BR-DSH-02-16 used to say "red for Expired", so every screen drawing this map
 * — the quotation list, the quotation editor, and the sales dashboard's Recent
 * Quotations — sat one word away from the dashboard's own rule. DECISION 9
 * (26 Aug 2026) confirmed amber and corrected the dashboard sentence, on
 * NFR-14's product-wide meanings: amber is WAITING, red is a PROBLEM, and R4
 * makes expiry neither fatal nor final ("expiry does not block conversion").
 * Because this map is the single source these screens share, the ruling
 * required no code change here — which is the point of it being shared.
 */
export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'converted';

const QUOTE_CLASSES: Record<QuoteStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  accepted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  expired: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  converted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
};

export function QuoteStatusChip({ status, className }: { status: QuoteStatus; className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn(CHIP, QUOTE_CLASSES[status], className)}>
      {t(`quotes.status.${status}`)}
    </span>
  );
}

/**
 * SAL-02 §7 — the six order chips: Draft grey, Confirmed blue, Partially
 * delivered amber, Fulfilled green, Closed green-faded, Cancelled red.
 */
export type OrderStatus =
  | 'draft'
  | 'confirmed'
  | 'partially_delivered'
  | 'fulfilled'
  | 'closed'
  | 'cancelled';

const ORDER_CLASSES: Record<OrderStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  confirmed: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  partially_delivered: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  fulfilled: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  // §7's "green-faded" — the fade is on the fill, so the word stays legible
  // (6.93:1, up from 3.43:1). See the note on DOC_CLASSES above.
  closed: 'bg-emerald-100/70 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
};

export function OrderStatusChip({ status, className }: { status: OrderStatus; className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn(CHIP, ORDER_CLASSES[status], className)}>
      {t(`orders.status.${status}`)}
    </span>
  );
}

/**
 * MD-01 §7 — the worst-first partner chip: Over limit red, Past Due amber,
 * Active green, Inactive grey. The server derives it from POSTED documents
 * only; the client just colours what it is told.
 */
const PARTNER_CLASSES: Record<PartnerResponse['status'], string> = {
  over_limit: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  past_due: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  inactive: 'bg-muted text-muted-foreground',
};

export function PartnerStatusChip({
  status,
  className,
}: {
  status: PartnerResponse['status'];
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <span className={cn(CHIP, PARTNER_CLASSES[status], className)}>
      {t(`partners.status.${status}`)}
    </span>
  );
}

/**
 * BR-MD-09-09 — "An invoice past its due date and not fully paid must show
 * the Past Due chip in amber, turning red after 30 days past due." The
 * SERVER derives the level from the company clock (N-16, `pastDueChip` in
 * apps/api platform/due-date.ts); this component colours what it is told —
 * the same division as the partner chip above. Null draws nothing.
 */
export function PastDueChip({
  level,
  className,
}: {
  level: 'amber' | 'red' | null;
  className?: string;
}) {
  const { t } = useTranslation();
  // Not-drawn covers null AND an older fixture's absent field alike.
  if (level !== 'amber' && level !== 'red') return null;
  return (
    <span
      className={cn(
        CHIP,
        level === 'red'
          ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300'
          : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
        className,
      )}
    >
      {t('common.pastDue')}
    </span>
  );
}

/** MD-01 §5 field 3 — each ticked type shows as a chip beside the name. */
export function PartnerTypeChips({
  partner,
  className,
}: {
  partner: Pick<PartnerResponse, 'isCustomer' | 'isSupplier'>;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <span className={cn('inline-flex gap-1', className)}>
      {partner.isCustomer ? (
        <span className={cn(CHIP, 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300')}>
          {t('partners.typeCustomer')}
        </span>
      ) : null}
      {partner.isSupplier ? (
        <span className={cn(CHIP, 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300')}>
          {t('partners.typeSupplier')}
        </span>
      ) : null}
    </span>
  );
}

/**
 * ── THE ONE PALETTE FOR EVERY DOCUMENT LIST (wave 3) ───────────────────────
 *
 * NFR-14 names five MEANINGS, and each map above is one screen spelling of
 * them. The shared list page (`components/document-list`) needs the meanings
 * themselves, so a page declares `{ confirmed: 'posted', fulfilled:
 * 'completed' }` and never a colour: the colour is looked up here, once, and
 * a ruling that moves "waiting" from amber to anything else is one edit.
 *
 *   draft      grey   — not started
 *   posted     blue   — active, in progress
 *   overdue    amber  — waiting on somebody, late but not lost
 *   completed  green  — finished, good, money in
 *   closed     green, faded on the fill — finished and filed (SAL-02 §7, and
 *              the purchase order register); the fade is on the FILL only,
 *              see the note on DOC_CLASSES above
 *   cancelled  red, faded on the fill — a problem, and spent
 *
 * Each entry REFERENCES a pair a map above already carries rather than
 * spelling it again: there is no new class string in this file, so
 * `theme-contrast.test.ts` and `faded-chip-contrast.test.ts` measure exactly
 * what they measured before, and a colour can only ever be changed in one
 * place.
 */
export type DocTone = 'draft' | 'posted' | 'overdue' | 'completed' | 'closed' | 'cancelled';

export const DOC_TONES: Readonly<Record<DocTone, string>> = {
  draft: DOC_CLASSES.draft,
  posted: DOC_CLASSES.posted,
  overdue: QUOTE_CLASSES.expired,
  completed: QUOTE_CLASSES.accepted,
  closed: ORDER_CLASSES.closed,
  cancelled: DOC_CLASSES.cancelled,
};

/**
 * The generic chip: a status, the meaning each status carries, and the
 * dictionary namespace whose `<ns>.status.<status>` keys name it. Every
 * existing chip above stays exactly as it is — this is for the pages that
 * configure `DocumentListPage` and have no bespoke chip of their own.
 */
export function StatusChip<Status extends string>({
  status,
  tones,
  ns,
  labelKey,
  className,
}: {
  status: Status;
  tones: Readonly<Record<Status, DocTone>>;
  /** The dictionary namespace: `orders` reads `orders.status.<status>`. */
  ns: string;
  /** A register whose words are not under `<ns>.status.*` names the key itself (PUR-06's `imports.stage_*`). */
  labelKey?: string | undefined;
  className?: string | undefined;
}) {
  const { t } = useTranslation();
  return (
    <span className={cn(CHIP, DOC_TONES[tones[status]], className)}>
      {t(labelKey ?? `${ns}.status.${status}`)}
    </span>
  );
}
